import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { Target, TrendingUp, PieChart, Lightbulb, Calendar, IndianRupee } from 'lucide-react';

const InteractiveGoalPlanner = () => {
  const [inputs, setInputs] = useState({
    goalName: 'My Financial Goal',
    targetAmount: 2500000,
    years: 5,
    expectedReturn: 12
  });
  const [result, setResult] = useState(null);

  const calculateGoal = useCallback(() => {
    const target = parseFloat(inputs.targetAmount);
    const r = parseFloat(inputs.expectedReturn) / 12 / 100;
    const n = parseInt(inputs.years) * 12;

    if (r === 0) {
      const monthlySIP = target / n;
      setResult({
        monthlySIP: Math.round(monthlySIP),
        totalInvested: Math.round(monthlySIP * n),
        wealthGained: 0,
        targetAmount: target
      });
      return;
    }

    const monthlySIP = target * r / (((Math.pow(1 + r, n) - 1)) * (1 + r));
    const totalInvested = monthlySIP * n;
    const wealthGained = target - totalInvested;

    setResult({
      monthlySIP: Math.round(monthlySIP),
      totalInvested: Math.round(totalInvested),
      wealthGained: Math.round(wealthGained),
      targetAmount: target
    });
  }, [inputs]);

  useEffect(() => {
    calculateGoal();
  }, [calculateGoal]);

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `\u20b9${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `\u20b9${(value / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const presetGoals = [
    { name: 'Car Purchase', amount: 1000000, years: 3 },
    { name: 'World Tour', amount: 500000, years: 2 },
    { name: 'Business Fund', amount: 5000000, years: 5 },
    { name: 'Dream Wedding', amount: 2500000, years: 4 }
  ];

  const sliderStyles = `
    .slider-purple::-webkit-slider-thumb {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #a855f7, #ec4899);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
    }
    .slider-purple::-moz-range-thumb {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: linear-gradient(135deg, #a855f7, #ec4899);
      cursor: pointer;
      box-shadow: 0 0 10px rgba(168, 85, 247, 0.5);
      border: none;
    }
  `;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      <style dangerouslySetInnerHTML={{ __html: sliderStyles }} />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mb-6">
              <Target className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Goal Planner Calculator
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Plan any financial goal with precision. Calculate exactly how much you need to invest monthly.
            </p>
          </div>

          <div className="mb-8">
            <p className="text-white/60 text-sm mb-3 text-center">Quick Presets:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {presetGoals.map((goal, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputs({ ...inputs, goalName: goal.name, targetAmount: goal.amount, years: goal.years })}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all text-sm"
                >
                  {goal.name}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-purple-400" />
                Define Your Goal
              </h2>
              
              <div className="space-y-8">
                <div>
                  <label className="text-white font-medium mb-2 block">Goal Name</label>
                  <input
                    type="text"
                    value={inputs.goalName}
                    onChange={(e) => setInputs({...inputs, goalName: e.target.value})}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    placeholder="e.g., Dream Vacation"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Target Amount</label>
                    <span className="text-purple-400 font-bold text-xl">{formatCurrency(inputs.targetAmount)}</span>
                  </div>
                  <input
                    type="range"
                    min="100000"
                    max="50000000"
                    step="100000"
                    value={inputs.targetAmount}
                    onChange={(e) => setInputs({...inputs, targetAmount: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-purple"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>₹1 L</span>
                    <span>₹5 Cr</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Time to Achieve Goal</label>
                    <span className="text-pink-400 font-bold text-xl">{inputs.years} Years</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={inputs.years}
                    onChange={(e) => setInputs({...inputs, years: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-purple"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>1 Year</span>
                    <span>30 Years</span>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-white font-medium">Expected Return (p.a.)</label>
                    <span className="text-green-400 font-bold text-xl">{inputs.expectedReturn}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="0.5"
                    value={inputs.expectedReturn}
                    onChange={(e) => setInputs({...inputs, expectedReturn: e.target.value})}
                    className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-purple"
                  />
                  <div className="flex justify-between text-white/40 text-xs mt-2">
                    <span>1%</span>
                    <span>20%</span>
                  </div>
                </div>
              </div>
            </div>

            {result && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-8 border border-purple-500/30">
                  <p className="text-white/60 text-sm mb-2">To achieve: {inputs.goalName}</p>
                  <div className="flex items-center gap-3 mb-4">
                    <IndianRupee className="w-6 h-6 text-purple-400" />
                    <h3 className="text-white/70 text-sm">Monthly SIP Required</h3>
                  </div>
                  <p className="text-5xl font-bold text-white mb-2">{formatCurrency(result.monthlySIP)}</p>
                  <p className="text-white/50 text-sm">for {inputs.years} years</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <PieChart className="w-4 h-4 text-white/50" />
                      <p className="text-white/50 text-xs">Total Investment</p>
                    </div>
                    <p className="text-2xl font-bold text-white">{formatCurrency(result.totalInvested)}</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <p className="text-white/50 text-xs">Wealth Gained</p>
                    </div>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(result.wealthGained)}</p>
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                  <p className="text-white/70 text-sm mb-2">Target Amount</p>
                  <p className="text-3xl font-bold text-white">{formatCurrency(result.targetAmount)}</p>
                  <div className="mt-4 h-3 bg-white/10 rounded-full overflow-hidden flex">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
                      style={{width: `${(result.totalInvested / result.targetAmount) * 100}%`}}
                    ></div>
                    <div 
                      className="bg-gradient-to-r from-pink-500 to-pink-600 transition-all duration-500"
                      style={{width: `${(result.wealthGained / result.targetAmount) * 100}%`}}
                    ></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs">
                    <span className="text-purple-400">Your Investment</span>
                    <span className="text-pink-400">Returns</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 backdrop-blur-xl rounded-2xl p-6 border border-amber-500/30">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-semibold mb-2">Smart Recommendation</p>
                      <p className="text-white/80 text-sm">
                        {inputs.years <= 3 
                          ? "For short-term goals, consider debt funds or hybrid funds for stability with moderate returns."
                          : inputs.years <= 7
                          ? "Medium-term goals work well with balanced advantage funds or large-cap equity funds."
                          : "For long-term goals, aggressive equity funds can maximize your wealth creation potential."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] transition-all duration-300"
            >
              Get Personalized Investment Advice
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InteractiveGoalPlanner;
